import {useMemo, useState} from "react";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useLocation} from "wouter";
import {Card} from "@/components/ui/card";
import {Button} from "@/components/ui/button";
import {Badge} from "@/components/ui/badge";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import {Switch} from "@/components/ui/switch";
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from "@/components/ui/dialog";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle} from "@/components/ui/alert-dialog";
import {useToast} from "@/hooks/use-toast";
import {chatThreadsService} from "@/services/chatThreadsService";
import {ragService} from "@/services/ragService";
import {
    MessageSquare,
    MessageCircle,
    MoreVertical,
    Pencil,
    Link2,
    Trash2,
    Sparkles,
    X,
    Lock,
    FolderOpen
} from "lucide-react";
import {formatDistanceToNow} from "date-fns";
import {ThreadLinkingDialog} from "./ThreadLinkingDialog";
import { EmbeddedSearchMark } from "@/components/EmbeddedSearchMark";
import type {ChatThread} from "@shared/schema";
import {useAuth} from "@/contexts/AuthContext";
import {
    effectiveItemActions,
    shouldRestrictEditorLinksToCurrentSharedSpace,
    type ItemOverrideMap,
    type SpaceMemberRole,
} from "@/services/sharedSpacePermissionService";

const THREAD_TITLE_MAX_LENGTH = 50;

function truncateTitle(title: string | undefined | null, maxLength: number): string {
    if (!title) return "Untitled Thread";
    return title.length <= maxLength ? title : title.slice(0, maxLength) + "...";
}

// Safe date parsing helper to prevent "Invalid time value" errors on mobile
function safeParseDate(dateValue: string | number | Date | null | undefined): Date | null {
    if (!dateValue) return null;
    try {
        const date = new Date(dateValue);
        // Check if date is valid (getTime returns NaN for invalid dates)
        return isNaN(date.getTime()) ? null : date;
    } catch {
        return null;
    }
}

interface ThreadItemProps {
    thread: ChatThread | (Omit<ChatThread, 'messages'> & { messageCount?: number });
    viewMode: "grid" | "list" | "compact";
    spaceId?: string;
    topicId?: string;
    topicNameMap?: Record<string | number, string>;
    embeddingStatusMap?: Record<string, boolean>;
    onRefresh?: () => void;
    /** For compact view: max length for title truncation */
    compactTitleMaxLength?: number;
    /** Optional callback when thread is clicked */
    onThreadClick?: (threadId: string, isLocked?: boolean) => void;
    /** Optional lineage label (e.g. "Space → Topic Name") shown in list view when provided */
    lineageLabel?: string;
    spaceMemberRole?: SpaceMemberRole | null;
    spaceOwnerUserId?: string | null;
    itemOverrides?: ItemOverrideMap;
    /** Mastermind space has active share (invited members). Used with spaceMemberRole for link restrictions. */
    isSharedSpace?: boolean;
}

export function ThreadItem({
    thread,
    viewMode,
    spaceId,
    topicId,
    topicNameMap = {},
    embeddingStatusMap = {},
    onRefresh,
    compactTitleMaxLength = 22,
    onThreadClick,
    lineageLabel,
    spaceMemberRole = null,
    spaceOwnerUserId = null,
    itemOverrides,
    isSharedSpace = false,
}: ThreadItemProps) {
    const {toast} = useToast();
    const {user} = useAuth();
    const [, setLocation] = useLocation();
    const queryClient = useQueryClient();
    
    const [threadToLink, setThreadToLink] = useState<any | null>(null);
    const [linkDialogTopicIds, setLinkDialogTopicIds] = useState<string[]>([]);
    const [linkDialogSpaceIds, setLinkDialogSpaceIds] = useState<string[]>([]);
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [renameValue, setRenameValue] = useState("");
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [threadToDelete, setThreadToDelete] = useState<any | null>(null);

    const threadUserId = (thread as any).userId as string | undefined;
    const itemActions =
        !spaceId
            ? {canView: true, canEdit: true, canDelete: true, canLink: true}
            : effectiveItemActions(
                  spaceMemberRole,
                  "thread",
                  thread.id,
                  threadUserId,
                  user?.id,
                  spaceOwnerUserId,
                  itemOverrides,
              );

    const restrictEditorLinksToCurrentSpace = useMemo(
        () =>
            shouldRestrictEditorLinksToCurrentSharedSpace(
                isSharedSpace,
                spaceMemberRole,
                spaceOwnerUserId,
                user?.id
            ),
        [isSharedSpace, spaceMemberRole, spaceOwnerUserId, user?.id]
    );

    const isLocked = (thread as any).isLocked === true || (thread as any).isLocked === "true";
    const isEmbedded = embeddingStatusMap[thread.id] === true;
    const includeInRag = (thread as { includeInRag?: boolean }).includeInRag !== false;

    // Get topicIds from thread (support both array and single value for backward compatibility)
    const getTopicIds = (thread: ChatThread | (Omit<ChatThread, 'messages'> & { messageCount?: number }) | null): string[] => {
        if (!thread) return [];
        
        // Check for topicIds array first (new format)

        if (Array.isArray((thread as any).topicIds)) {
            return (thread as any).topicIds.filter((id: any) => id != null).map((id: any) => String(id));
        }
        
        // Fallback to single topicId (backward compatibility)
        if ((thread as any).topicId) {
            return [String((thread as any).topicId)];
        }
        
        return [];
    };

    // Get spaceIds from thread (support both array and single value for backward compatibility)
    const getSpaceIds = (thread: ChatThread | (Omit<ChatThread, 'messages'> & { messageCount?: number })): string[] => {
        const linkedSpaceIds = (thread as any).linkedSpaceIds;
        
        // Check for linkedSpaceIds array first (new format - already jsonb)
        if (Array.isArray(linkedSpaceIds)) {
            return linkedSpaceIds.filter((id: any) => id != null).map((id: any) => String(id));
        }
        
        // Fallback to single value if it exists
        if (linkedSpaceIds) {
            return [String(linkedSpaceIds)];
        }
        
        return [];
    };

    // Rename mutation
    const renameMutation = useMutation({
        mutationFn: async ({threadId, title}: {threadId: string; title: string}) => {
            return await chatThreadsService.updateChatThread(threadId, {title}, undefined, spaceId ?? null);
        },
        onSuccess: async () => {
            await Promise.all([
                queryClient.refetchQueries({queryKey: ["/api/chat-thread-summaries"], exact: false}),
                queryClient.refetchQueries({queryKey: ["/api/chat-threads"], exact: false}),
            ]);
            if (spaceId) {
                queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "threads"]});
            }
            if (topicId) {
                queryClient.invalidateQueries({queryKey: ["/api/topics", topicId, "threads"]});
            }
            queryClient.invalidateQueries({queryKey: ["/api/topics"]});
            toast({title: "Thread Renamed", description: "Thread title updated"});
            setRenameDialogOpen(false);
            setRenameValue("");
            onRefresh?.();
        },
        onError: (error: any) => {
            toast({title: "Rename Failed", description: error?.message || "Could not rename thread", variant: "destructive"});
        },
    });

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: async (threadId: string) => {
            return await chatThreadsService.deleteChatThread(threadId, spaceId ?? null);
        },
        onSuccess: async () => {
            // Optimistically remove from cache
            queryClient.setQueriesData<any[]>(
                {queryKey: ["/api/chat-thread-summaries"], exact: false},
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.filter(t => t.id !== thread.id);
                }
            );

            await Promise.all([
                queryClient.refetchQueries({queryKey: ["/api/chat-thread-summaries"], exact: false}),
                queryClient.refetchQueries({queryKey: ["/api/chat-threads"], exact: false}),
            ]);
            if (spaceId) {
                queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "threads"]});
            }
            if (topicId) {
                queryClient.invalidateQueries({queryKey: ["/api/topics", topicId, "threads"]});
            }
            queryClient.invalidateQueries({queryKey: ["/api/topics"]});
            toast({title: "Thread Deleted", description: "Thread has been removed"});
            setDeleteDialogOpen(false);
            setThreadToDelete(null);
            onRefresh?.();
        },
        onError: (error: any) => {
            toast({title: "Delete Failed", description: error?.message || "Could not delete thread", variant: "destructive"});
        },
    });

    // Update spaces mutation
    const updateSpacesMutation = useMutation({
        mutationFn: async ({threadId, spaceIds}: {threadId: string; spaceIds: string[]}) => {
            return await chatThreadsService.updateChatThread(threadId, {linkedSpaceIds: spaceIds}, undefined, spaceId ?? null);
        },
        onSuccess: async (_, variables) => {
            queryClient.setQueriesData<any[]>(
                {queryKey: ["/api/chat-thread-summaries"], exact: false},
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.map(t =>
                        t.id === variables.threadId
                            ? {...t, linkedSpaceIds: variables.spaceIds}
                            : t
                    );
                }
            );

            await Promise.all([
                queryClient.refetchQueries({queryKey: ["/api/chat-thread-summaries"], exact: false}),
                queryClient.refetchQueries({queryKey: ["/api/chat-threads"], exact: false}),
            ]);
            if (spaceId) {
                queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "threads"]});
            }
            queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "topics"]});
            // Invalidate all topic-related queries including topic detail queries
            queryClient.invalidateQueries({queryKey: ["/api/topics"]});
            // Also invalidate specific topic detail queries (pattern: /api/topics/:id)
            queryClient.invalidateQueries({predicate: (query) => {
                const key = query.queryKey[0];
                return typeof key === 'string' && key.startsWith('/api/topics/');
            }});
            toast({title: "Spaces Updated", description: "Thread linked spaces updated"});
            onRefresh?.();
        },
        onError: async (error: any) => {
            await queryClient.refetchQueries({queryKey: ["/api/chat-thread-summaries"], exact: false});
            toast({title: "Update Failed", description: error?.message || "Could not update linked spaces", variant: "destructive"});
        },
    });

    // Update topics mutation (for multiple topics)
    const updateTopicsMutation = useMutation({
        mutationFn: async ({threadId, topicIds}: {threadId: string; topicIds: string[]}) => {
            const result = await chatThreadsService.updateChatThread(threadId, {topicIds} as any, undefined, spaceId ?? null);
            if (!result) {
                throw new Error("The topic link was not saved (thread not found or no permission).");
            }
            return result;
        },
        onSuccess: async (_, variables) => {
            queryClient.setQueriesData<any[]>(
                {queryKey: ["/api/chat-thread-summaries"], exact: false},
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.map(t =>
                        t.id === variables.threadId
                            ? {...t, topicIds: variables.topicIds, topicId: null}
                            : t
                    );
                }
            );

            await Promise.all([
                queryClient.refetchQueries({queryKey: ["/api/chat-thread-summaries"], exact: false}),
                queryClient.refetchQueries({queryKey: ["/api/chat-threads"], exact: false}),
            ]);
            if (spaceId) {
                queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "threads"]});
                queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "topics"]});
            }
            // Invalidate topics for all affected topics
            variables.topicIds.forEach(tid => {
                queryClient.invalidateQueries({queryKey: ["/api/topics", tid, "threads"]});
            });
            queryClient.invalidateQueries({queryKey: ["/api/topics"]});
            // Also invalidate specific topic detail queries (pattern: /api/topics/:id)
            queryClient.invalidateQueries({predicate: (query) => {
                const key = query.queryKey[0];
                return typeof key === 'string' && key.startsWith('/api/topics/');
            }});
            toast({title: "Topics Updated", description: "Thread topics updated"});
            onRefresh?.();
        },
        onError: async (error: any) => {
            await queryClient.refetchQueries({queryKey: ["/api/chat-thread-summaries"], exact: false});
            toast({title: "Update Failed", description: error?.message || "Could not update thread topics", variant: "destructive"});
        },
    });

    // Update topic mutation (for backward compatibility - single topic)
    const updateTopicMutation = useMutation({
        mutationFn: async ({threadId, topicId}: {threadId: string; topicId: string | null}) => {
            const result = await chatThreadsService.updateChatThread(threadId, {topicId}, undefined, spaceId ?? null);
            if (!result) {
                throw new Error("The topic link was not saved (thread not found or no permission).");
            }
            return result;
        },
        onSuccess: async (_, variables) => {
            queryClient.setQueriesData<any[]>(
                {queryKey: ["/api/chat-thread-summaries"], exact: false},
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.map(t =>
                        t.id === variables.threadId
                            ? {...t, topicId: variables.topicId || null, topicIds: variables.topicId ? [variables.topicId] : undefined}
                            : t
                    );
                }
            );

            await Promise.all([
                queryClient.refetchQueries({queryKey: ["/api/chat-thread-summaries"], exact: false}),
                queryClient.refetchQueries({queryKey: ["/api/chat-threads"], exact: false}),
            ]);
            if (spaceId) {
                queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "threads"]});
                queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "topics"]});
            }
            if (variables.topicId) {
                queryClient.invalidateQueries({queryKey: ["/api/topics", variables.topicId, "threads"]});
            }
            queryClient.invalidateQueries({queryKey: ["/api/topics"]});
            // Also invalidate specific topic detail queries (pattern: /api/topics/:id)
            queryClient.invalidateQueries({predicate: (query) => {
                const key = query.queryKey[0];
                return typeof key === 'string' && key.startsWith('/api/topics/');
            }});
            toast({title: "Topic Updated", description: "Thread topic updated"});
            onRefresh?.();
        },
        onError: async (error: any) => {
            await queryClient.refetchQueries({queryKey: ["/api/chat-thread-summaries"], exact: false});
            toast({title: "Update Failed", description: error?.message || "Could not update thread topic", variant: "destructive"});
        },
    });

    // Update tags mutation
    const updateTagsMutation = useMutation({
        mutationFn: async ({threadId, tags}: {threadId: string; tags: string[]}) => {
            return await chatThreadsService.updateChatThread(threadId, {tags}, undefined, spaceId ?? null);
        },
        onSuccess: async (_, variables) => {
            queryClient.setQueriesData<any[]>(
                {queryKey: ["/api/chat-thread-summaries"], exact: false},
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.map(t =>
                        t.id === variables.threadId
                            ? {...t, tags: variables.tags}
                            : t
                    );
                }
            );

            await Promise.all([
                queryClient.refetchQueries({queryKey: ["/api/chat-thread-summaries"], exact: false}),
                queryClient.refetchQueries({queryKey: ["/api/chat-threads"], exact: false}),
            ]);
            if (spaceId) {
                queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "threads"]});
            }
            queryClient.invalidateQueries({queryKey: ["/api/topics"]});
            toast({title: "Tags Updated", description: "Thread tags updated"});
            onRefresh?.();
        },
        onError: async (error: any) => {
            await queryClient.refetchQueries({queryKey: ["/api/chat-thread-summaries"], exact: false});
            toast({title: "Update Failed", description: error?.message || "Could not update thread tags", variant: "destructive"});
        },
    });

    // Handlers
    const handleRename = () => {
        setRenameValue(thread.title || "");
        setRenameDialogOpen(true);
    };

    const handleConfirmRename = () => {
        if (renameValue.trim()) {
            renameMutation.mutate({threadId: thread.id, title: renameValue.trim()});
        }
    };

    const handleLinkToSpaceTopic = async () => {
        // Check if thread needs migration from old format to new array format
        const currentTopicIds = getTopicIds(thread);
        const currentSpaceIds = getSpaceIds(thread);
        
        console.log("🔍 handleLinkToSpaceTopic - BEFORE migration:", {
            threadId: thread.id,
            currentTopicIds,
            currentSpaceIds,
            threadTopicIds: (thread as any).topicIds,
            threadTopicId: (thread as any).topicId,
            threadLinkedSpaceIds: (thread as any).linkedSpaceIds
        });
        
        // Check if we have old topicId but no topicIds array
        const hasOldTopicId = (thread as any).topicId && !Array.isArray((thread as any).topicIds);
        
        let needsMigration = false;
        let migratedTopicIds: string[] = [];
        
        if (hasOldTopicId) {
            needsMigration = true;
            migratedTopicIds = currentTopicIds; // Already includes the old topicId
        } else {
            migratedTopicIds = currentTopicIds;
        }
        
        console.log("🔍 handleLinkToSpaceTopic - AFTER processing:", {
            needsMigration,
            migratedTopicIds,
            currentSpaceIds
        });
        
        // Store topicIds and spaceIds in state for the dialog (alternative approach)
        setLinkDialogTopicIds(migratedTopicIds);
        setLinkDialogSpaceIds(currentSpaceIds);
        
        // Create migrated thread structure for the modal (immediate, no API wait)
        // Always set topicIds as an array (even if empty) so getTopicIds() can read it
        const migratedThread = {
            ...thread,
            linkedSpaceIds: currentSpaceIds,
            topicIds: migratedTopicIds, // Always an array, never undefined
            topicId: null, // Clear old topicId when using topicIds array
            tags: (thread as any).tags || [],
        } as any;
        
        console.log("🔍 handleLinkToSpaceTopic - migratedThread:", {
            topicIds: migratedThread.topicIds,
            linkedSpaceIds: migratedThread.linkedSpaceIds
        });
        
        // Set the migrated thread for the modal immediately
        setThreadToLink(migratedThread);
        
        // Migrate the thread in the background (update database) if needed
        if (needsMigration) {
            try {
                await chatThreadsService.updateChatThread(thread.id, {topicIds: migratedTopicIds} as any, undefined, spaceId ?? null);
                
                // Optimistically update the cache
                queryClient.setQueriesData<any[]>(
                    {queryKey: ["/api/chat-thread-summaries"], exact: false},
                    (oldData) => {
                        if (!oldData) return oldData;
                        return oldData.map(t => {
                            if (t.id === thread.id) {
                                return {
                                    ...t,
                                    topicIds: migratedTopicIds,
                                    topicId: null
                                };
                            }
                            return t;
                        });
                    }
                );
                
                // Refetch to ensure consistency
                await queryClient.refetchQueries({queryKey: ["/api/chat-thread-summaries"], exact: false});
                onRefresh?.();
            } catch (error: any) {
                console.error("Failed to migrate thread to new format:", error);
                // Modal already has the migrated structure, so it will still work correctly
            }
        }
    };

    const handleUpdateSpaces = (spaceIds: string[]) => {
        updateSpacesMutation.mutate({threadId: thread.id, spaceIds});
    };

    const handleUpdateTopics = (selectedTopicIds: string[]) => {
        updateTopicsMutation.mutate({threadId: thread.id, topicIds: selectedTopicIds});
    };

    const handleUpdateTopic = (topicId: string | null) => {
        updateTopicMutation.mutate({threadId: thread.id, topicId});
    };

    const handleUpdateTags = (tags: string[]) => {
        updateTagsMutation.mutate({threadId: thread.id, tags});
    };

    const handleEmbedForSearch = async () => {
        try {
            await ragService.embedThread(thread.id);
            toast({title: "Embedded for search", description: `"${thread.title || "Thread"}" is now searchable by meaning.`});
            queryClient.invalidateQueries({queryKey: ["thread-embedding-status"]});
            queryClient.invalidateQueries({queryKey: ["thread-embedding-status-space"]});
            onRefresh?.();
        } catch (err: any) {
            toast({title: "Embed failed", description: err?.message || "Could not embed thread.", variant: "destructive"});
        }
    };

    const handleReembedForSearch = async () => {
        try {
            await ragService.embedThread(thread.id);
            toast({title: "Re-embedded for search", description: `"${thread.title || "Thread"}" is updated for semantic search.`});
            queryClient.invalidateQueries({queryKey: ["thread-embedding-status"]});
            queryClient.invalidateQueries({queryKey: ["thread-embedding-status-space"]});
            onRefresh?.();
        } catch (err: any) {
            toast({title: "Re-embed failed", description: err?.message || "Could not re-embed thread.", variant: "destructive"});
        }
    };

    const handleRemoveFromSearch = async () => {
        try {
            await ragService.deleteThreadEmbeddings(thread.id);
            toast({title: "Removed from search", description: `"${thread.title || "Thread"}" is no longer embedded.`});
            queryClient.invalidateQueries({queryKey: ["thread-embedding-status"]});
            queryClient.invalidateQueries({queryKey: ["thread-embedding-status-space"]});
            onRefresh?.();
        } catch (err: any) {
            toast({title: "Remove failed", description: err?.message || "Could not remove embeddings.", variant: "destructive"});
        }
    };

    const handleToggleIncludeInRag = async (value: boolean) => {
        try {
            await chatThreadsService.updateChatThread(thread.id, {includeInRag: value} as any, undefined, spaceId ?? null);
            toast({title: value ? "Included in RAG" : "Excluded from RAG", description: `"${thread.title || "Thread"}" is ${value ? "in" : "out of"} RAG retrieval.`});
            queryClient.invalidateQueries({queryKey: ["/api/chat-thread-summaries"]});
            queryClient.invalidateQueries({queryKey: ["/api/chat-threads"]});
            onRefresh?.();
        } catch (err: any) {
            toast({title: "Update failed", description: err?.message || "Could not update thread.", variant: "destructive"});
        }
    };

    const handleDelete = () => {
        setThreadToDelete(thread);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = () => {
        if (threadToDelete) {
            deleteMutation.mutate(threadToDelete.id);
        }
    };

    const handleClick = () => {
        if (onThreadClick) {
            onThreadClick(thread.id, isLocked);
        } else {
            const url = `/chat?threadId=${thread.id}${spaceId ? `&spaceId=${spaceId}` : ""}${topicId ? `&topicId=${topicId}` : ""}`;
            setLocation(url);
        }
    };

    const renderDropdownMenu = () => {
        if (!itemActions.canEdit && !itemActions.canDelete) {
            return null;
        }
        return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button
                    variant="ghost"
                    size="icon"
                    className={viewMode === "compact" ? "h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" : "h-7 w-7 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity"}
                    data-testid={`button-thread-menu-${thread.id}`}
                >
                    <MoreVertical className={viewMode === "compact" ? "h-3 w-3" : "h-3.5 w-3.5"}/>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                {itemActions.canEdit && (
                <DropdownMenuItem onClick={handleRename}>
                    <Pencil className="h-4 w-4 mr-2"/> Rename
                </DropdownMenuItem>
                )}
                {itemActions.canLink && (
                <DropdownMenuItem onClick={handleLinkToSpaceTopic}>
                    <Link2 className="h-4 w-4 mr-2"/> Link to Space/Topic
                </DropdownMenuItem>
                )}
                {itemActions.canEdit && <DropdownMenuSeparator/>}
                {itemActions.canEdit && (isEmbedded ? (
                    <>
                    <DropdownMenuItem onClick={handleReembedForSearch}>
                        <Sparkles className="h-4 w-4 mr-2"/> Re-embed for search
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { void handleRemoveFromSearch(); }}>
                        <X className="h-4 w-4 mr-2"/> Remove from search
                    </DropdownMenuItem>
                    </>
                ) : (
                    <DropdownMenuItem onClick={handleEmbedForSearch}>
                        <Sparkles className="h-4 w-4 mr-2"/> Embed for search
                    </DropdownMenuItem>
                ))}
                {itemActions.canEdit && (
                <DropdownMenuItem
                    onSelect={(e) => e.preventDefault()}
                    className="flex items-center justify-between gap-4 cursor-default"
                >
                    <span>Include</span>
                    <Switch
                        checked={includeInRag}
                        onCheckedChange={handleToggleIncludeInRag}
                        onClick={(e) => e.stopPropagation()}
                    />
                </DropdownMenuItem>
                )}
                {itemActions.canDelete && <DropdownMenuSeparator/>}
                {itemActions.canDelete && (
                <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={handleDelete}
                >
                    <Trash2 className="h-4 w-4 mr-2"/> Delete
                </DropdownMenuItem>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
        );
    };

    // Grid view
    if (viewMode === "grid") {
        return (
            <>
                <Card
                    className="p-3 cursor-pointer hover-elevate relative group/card"
                    onClick={handleClick}
                    data-testid={`card-thread-${thread.id}`}
                >
                    <div className="flex items-start gap-3">
                        {isLocked ? (
                            <Lock className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5"/>
                        ) : (
                            <MessageSquare className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5"/>
                        )}
                        <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate flex items-center gap-1.5" title={thread.title || "Untitled Thread"}>
                                <span className="truncate">{truncateTitle(thread.title || "Untitled Thread", THREAD_TITLE_MAX_LENGTH)}</span>
                                {isEmbedded && <EmbeddedSearchMark />}
                            </div>
                            {thread.topicId && topicNameMap[thread.topicId] && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 mt-1 flex-shrink-0 gap-1">
                                    <FolderOpen className="w-2.5 h-2.5"/>
                                    <span className="truncate max-w-[100px]">{topicNameMap[thread.topicId]}</span>
                                </Badge>
                            )}
                            {thread.updatedAt && (() => {
                                const date = safeParseDate(thread.updatedAt);
                                return date ? (
                                    <div className="text-xs text-muted-foreground mt-2">
                                        {date.toLocaleDateString()}
                                    </div>
                                ) : null;
                            })()}
                        </div>
                        {renderDropdownMenu()}
                    </div>
                </Card>

                {/* Dialogs */}
                <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Rename Thread</DialogTitle>
                        </DialogHeader>
                        <div className="py-4">
                            <Label htmlFor="thread-rename-input">Thread Name</Label>
                            <Input
                                id="thread-rename-input"
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                placeholder="Enter thread name..."
                                className="mt-2"
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && renameValue.trim()) {
                                        handleConfirmRename();
                                    }
                                }}
                            />
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button onClick={handleConfirmRename} disabled={!renameValue.trim() || renameMutation.isPending}>
                                {renameMutation.isPending ? "Saving..." : "Save"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Delete Thread?</AlertDialogTitle>
                            <AlertDialogDescription>
                                Are you sure you want to delete "{threadToDelete?.title || "this thread"}"?
                                This will permanently delete the thread and all its messages.
                                This action cannot be undone.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={handleConfirmDelete}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                                {deleteMutation.isPending ? "Deleting..." : "Delete Thread"}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {threadToLink && (() => {
                    // Get topicIds and spaceIds - use state values first, fallback to getTopicIds/getSpaceIds
                    const dialogTopicIds = linkDialogTopicIds.length > 0 || getTopicIds(threadToLink).length > 0 
                        ? (linkDialogTopicIds.length > 0 ? linkDialogTopicIds : getTopicIds(threadToLink))
                        : [];
                    const dialogSpaceIds = linkDialogSpaceIds.length > 0 || getSpaceIds(threadToLink).length > 0
                        ? (linkDialogSpaceIds.length > 0 ? linkDialogSpaceIds : getSpaceIds(threadToLink))
                        : [];
                    
                    return (
                        <ThreadLinkingDialog
                            open={!!threadToLink}
                            onOpenChange={(open) => {
                                if (!open) {
                                    setThreadToLink(null);
                                    setLinkDialogTopicIds([]);
                                    setLinkDialogSpaceIds([]);
                                }
                            }}
                            linkedSpaceIds={dialogSpaceIds}
                            topicId={null}
                            topicIds={dialogTopicIds}
                            tags={(threadToLink as any).tags || []}
                            allowMultipleTopics={true}
                            onUpdateSpaces={handleUpdateSpaces}
                            onUpdateTopics={handleUpdateTopics}
                            onUpdateTags={handleUpdateTags}
                            editorLinksRestrictedToSpaceId={restrictEditorLinksToCurrentSpace && spaceId ? spaceId : null}
                        />
                    );
                })()}
            </>
        );
    }

    // List view
    if (viewMode === "list") {
        const showLineageColumn = lineageLabel != null && lineageLabel !== "";
        return (
            <>
                <div
                    className={`grid gap-3 items-center px-3 py-2.5 hover-elevate group w-full min-w-0 max-w-full ${showLineageColumn ? "grid-cols-[auto_1fr_auto_auto_auto]" : "grid-cols-[auto_1fr_auto_auto]"}`}
                    data-testid={`row-thread-${thread.id}`}
                    style={{width: '100%'}}
                >
                    {/* Icon */}
                    <div className="p-1.5 rounded-md bg-primary/10">
                        {isLocked ? (
                            <Lock className="h-4 w-4 text-amber-500"/>
                        ) : (
                            <MessageSquare className="h-4 w-4 text-primary"/>
                        )}
                    </div>

                    {/* Title */}
                    <div className="min-w-0 overflow-hidden flex items-center gap-2">
                        <a
                            href={`/chat?threadId=${thread.id}${spaceId ? `&spaceId=${spaceId}` : ""}${topicId ? `&topicId=${topicId}` : ""}`}
                            onClick={(e) => {
                                e.preventDefault();
                                handleClick();
                            }}
                            className="font-medium text-sm truncate cursor-pointer hover:text-primary transition-colors block min-w-0"
                            data-testid={`link-thread-${thread.id}`}
                            title={thread.title || "Untitled Thread"}
                        >
                            {thread.title || "Untitled Thread"}
                        </a>
                        {isEmbedded && <EmbeddedSearchMark />}
                        {!showLineageColumn && thread.topicId && topicNameMap[thread.topicId] && (
                            <span className="text-xs text-muted-foreground truncate shrink-0 max-w-[100px] sm:max-w-[120px]">
                                <span className="hidden sm:inline text-muted-foreground/60">• </span>
                                {topicNameMap[thread.topicId]}
                            </span>
                        )}
                    </div>

                    {/* Lineage (when provided, e.g. Space → Topic) */}
                    {showLineageColumn && (
                        <div className="text-xs text-muted-foreground truncate min-w-0 max-w-[140px] sm:max-w-[180px]" title={lineageLabel}>
                            {lineageLabel}
                        </div>
                    )}

                    {/* Date */}
                    <div className="flex items-center gap-2 shrink-0">
                        {thread.updatedAt && (() => {
                            const date = safeParseDate(thread.updatedAt);
                            return date ? (
                                <div className="text-xs text-muted-foreground hidden xl:block w-20 text-right flex-shrink-0">
                                    {formatDistanceToNow(date, {addSuffix: true})}
                                </div>
                            ) : null;
                        })()}
                    </div>

                    {/* Actions */}
                    <div className="w-8 flex-shrink-0 flex justify-end">
                        {renderDropdownMenu()}
                    </div>
                </div>

                {/* Dialogs - same as grid view */}
                <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Rename Thread</DialogTitle>
                        </DialogHeader>
                        <div className="py-4">
                            <Label htmlFor="thread-rename-input">Thread Name</Label>
                            <Input
                                id="thread-rename-input"
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                placeholder="Enter thread name..."
                                className="mt-2"
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && renameValue.trim()) {
                                        handleConfirmRename();
                                    }
                                }}
                            />
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button onClick={handleConfirmRename} disabled={!renameValue.trim() || renameMutation.isPending}>
                                {renameMutation.isPending ? "Saving..." : "Save"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Delete Thread?</AlertDialogTitle>
                            <AlertDialogDescription>
                                Are you sure you want to delete "{threadToDelete?.title || "this thread"}"?
                                This will permanently delete the thread and all its messages.
                                This action cannot be undone.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={handleConfirmDelete}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                                {deleteMutation.isPending ? "Deleting..." : "Delete Thread"}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {threadToLink && (() => {
                    // Get topicIds and spaceIds - use state values first, fallback to getTopicIds/getSpaceIds
                    const dialogTopicIds = linkDialogTopicIds.length > 0 || getTopicIds(threadToLink).length > 0 
                        ? (linkDialogTopicIds.length > 0 ? linkDialogTopicIds : getTopicIds(threadToLink))
                        : [];
                    const dialogSpaceIds = linkDialogSpaceIds.length > 0 || getSpaceIds(threadToLink).length > 0
                        ? (linkDialogSpaceIds.length > 0 ? linkDialogSpaceIds : getSpaceIds(threadToLink))
                        : [];
                    
                    console.log("🔍 ThreadLinkingDialog - BEFORE passing props:", {
                        threadToLink: threadToLink ? {
                            id: threadToLink.id,
                            topicIds: (threadToLink as any).topicIds,
                            topicId: (threadToLink as any).topicId,
                            linkedSpaceIds: (threadToLink as any).linkedSpaceIds
                        } : null,
                        linkDialogTopicIds,
                        linkDialogSpaceIds,
                        getTopicIdsResult: getTopicIds(threadToLink),
                        getSpaceIdsResult: getSpaceIds(threadToLink),
                        finalDialogTopicIds: dialogTopicIds,
                        finalDialogSpaceIds: dialogSpaceIds
                    });
                    
                    return (
                        <ThreadLinkingDialog
                            open={!!threadToLink}
                            onOpenChange={(open) => {
                                if (!open) {
                                    setThreadToLink(null);
                                    setLinkDialogTopicIds([]);
                                    setLinkDialogSpaceIds([]);
                                }
                            }}
                            linkedSpaceIds={dialogSpaceIds}
                            topicId={null}
                            topicIds={dialogTopicIds}
                            tags={(threadToLink as any).tags || []}
                            allowMultipleTopics={true}
                            onUpdateSpaces={handleUpdateSpaces}
                            onUpdateTopics={handleUpdateTopics}
                            onUpdateTags={handleUpdateTags}
                            editorLinksRestrictedToSpaceId={restrictEditorLinksToCurrentSpace && spaceId ? spaceId : null}
                        />
                    );
                })()}
            </>
        );
    }

    // Compact view
    return (
        <>
            <div
                className="grid grid-cols-[auto_1fr_auto] gap-2 items-center p-2 rounded-md hover:bg-muted/50 cursor-pointer text-sm group w-full min-w-0 max-w-full"
                onClick={handleClick}
                data-testid={`thread-${thread.id}`}
            >
                {isLocked ? (
                    <Lock className="w-3.5 h-3.5 text-amber-500 flex-shrink-0"/>
                ) : (
                    <MessageCircle className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0"/>
                )}
                <div className="min-w-0 overflow-hidden flex items-center gap-1.5">
                    <span className="truncate block font-medium" title={thread.title || "Untitled Thread"}>
                        {thread.title || "Untitled Thread"}
                    </span>
                    {isEmbedded && <EmbeddedSearchMark className="h-3 w-3" />}
                </div>
                <div className="flex items-center gap-1 shrink-0 justify-end">
                    {renderDropdownMenu()}
                </div>
            </div>

            {/* Dialogs - same as other views */}
            <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Rename Thread</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <Label htmlFor="thread-rename-input">Thread Name</Label>
                        <Input
                            id="thread-rename-input"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            placeholder="Enter thread name..."
                            className="mt-2"
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && renameValue.trim()) {
                                    handleConfirmRename();
                                }
                            }}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleConfirmRename} disabled={!renameValue.trim() || renameMutation.isPending}>
                            {renameMutation.isPending ? "Saving..." : "Save"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Thread?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete "{threadToDelete?.title || "this thread"}"?
                            This will permanently delete the thread and all its messages.
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleConfirmDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {deleteMutation.isPending ? "Deleting..." : "Delete Thread"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {threadToLink && (() => {
                // Get topicIds and spaceIds - use state values first, fallback to getTopicIds/getSpaceIds
                const dialogTopicIds = linkDialogTopicIds.length > 0 || getTopicIds(threadToLink).length > 0 
                    ? (linkDialogTopicIds.length > 0 ? linkDialogTopicIds : getTopicIds(threadToLink))
                    : [];
                const dialogSpaceIds = linkDialogSpaceIds.length > 0 || getSpaceIds(threadToLink).length > 0
                    ? (linkDialogSpaceIds.length > 0 ? linkDialogSpaceIds : getSpaceIds(threadToLink))
                    : [];
                
                console.log("🔍 ThreadLinkingDialog (compact) - BEFORE passing props:", {
                    threadToLink: threadToLink ? {
                        id: threadToLink.id,
                        topicIds: (threadToLink as any).topicIds,
                        topicId: (threadToLink as any).topicId,
                        linkedSpaceIds: (threadToLink as any).linkedSpaceIds
                    } : null,
                    linkDialogTopicIds,
                    linkDialogSpaceIds,
                    getTopicIdsResult: getTopicIds(threadToLink),
                    getSpaceIdsResult: getSpaceIds(threadToLink),
                    finalDialogTopicIds: dialogTopicIds,
                    finalDialogSpaceIds: dialogSpaceIds
                });
                
                return (
                    <ThreadLinkingDialog
                        open={!!threadToLink}
                        onOpenChange={(open) => {
                            if (!open) {
                                setThreadToLink(null);
                                setLinkDialogTopicIds([]);
                                setLinkDialogSpaceIds([]);
                            }
                        }}
                        linkedSpaceIds={dialogSpaceIds}
                        topicId={null}
                        topicIds={dialogTopicIds}
                        tags={(threadToLink as any).tags || []}
                        allowMultipleTopics={true}
                        onUpdateSpaces={handleUpdateSpaces}
                        onUpdateTopics={handleUpdateTopics}
                        onUpdateTags={handleUpdateTags}
                        editorLinksRestrictedToSpaceId={restrictEditorLinksToCurrentSpace && spaceId ? spaceId : null}
                    />
                );
            })()}
        </>
    );
}
